import React, { useState, useEffect } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import DeleteModal from '../../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { Pagination } from '../../../components/Pagination';
import { accessService } from '../../../services/central-api/access';
import { ClientError } from '../../../services/central-api/base-service';
import { AccessParamList } from '../../../typings/Params';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import MyDataTable from '../../../components/MyDataTable';
import FilterInput from '../../../components/Filter/components/FilterInput';
import CustomButton from '../../../components/CustomButton';
import Filter from './Filters';
import useFilters from '../../../components/Filter/hooks/useFilters';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import DropdownButton from '../../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import RoleModal from './RoleModal';
import { RootNavigation } from '../../../Routes';

export default function Roles({ navigation }: { navigation: RootNavigation; }) {
    const route = useRoute<RouteProp<AccessParamList, 'Roles'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<AccessParamList['Roles']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const [selectedRole, setSelectedRole] = useState<Role>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [roles, setRoles] = useState<Role[]>([]);
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [totalItems, setTotalItems] = useState(0);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['accessRoles', filters],
        queryFn: () => accessService.getPaginatedRoles(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setRoles(data.rows);
        setTotalItems(data.count);
    }, [data]);

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));

            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
        }
    }

    async function confirmDelete(role: Role) {
        try {
            await accessService.deleteRole(role.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            setDeleteModalVisible(false);
            refetch();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return <Container>
        <Header
            title={translate('roles')}
            actions={<CustomButton
                text={translate('register')}
                onPress={() => {
                    setSelectedRole(undefined);
                    setIsModalVisible(true);
                }}
            />}
        />
        <Card>
            <DataTableHeader
                zIndex={2}
                title={translate('registeredRoles')}
                filter={<Filter
                    filters={filters}
                    setFilters={setFilters}
                    textFilter={textFilter}
                    setTextFilter={setTextFilter} />}
                filterInput={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={text => {
                        setTextFilter(text);
                    }}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: translate('name'),
                        selector: row => row.name,
                        wrap: true,
                        grow: 3
                    },
                    {
                        name: translate('actingBody'),
                        selector: row => row.actingBody?.name ?? '',
                        wrap: true,
                        grow: 1,
                    },
                    {
                        button: true,
                        cell: row => <DropdownButton
                            actions={[{
                                content: translate('editInformation'),
                                onPress: () => {
                                    setSelectedRole(row);
                                    setIsModalVisible(true);
                                }
                            }, {
                                content: translate('delete'),
                                variant: 'danger',
                                onPress: () => {
                                    (row); setSelectedRole(row);
                                    setDeleteModalVisible(true);
                                },
                            }]}
                        />
                    }]}
                    data={roles}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    totalItems={totalItems}
                    currentPage={filters.page}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                />}
            />
        </Card>
        {selectedRole &&
            <DeleteModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                itemName={selectedRole.name}
                onSubmit={async () => {
                    await confirmDelete(selectedRole);
                }}
            />
        }

        <RoleModal
            setModalVisible={setIsModalVisible}
            isModalVisible={isModalVisible}
            roleId={selectedRole?.id}
            onClose={refetch}
        />
    </Container>;
}
